import { BookingsStore } from 'src/app/stores/bookings.store';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { finalize, Subject } from 'rxjs';
import {
  AttendeeInformation,
  CalendarEvent,
  Place,
} from 'src/app/interfaces/interfaces';
import { ApiService } from 'src/app/services/api-services/api.service';
import { DatetimeFormatService } from 'src/app/services/datetime-format-service/datetime-format.service';

interface Proposal {
  upn: string;
  name: string;
  start: string;
  end: string;
  img: string;
  initials: string;
  availabilityKey: string;
}

@Component({
  selector: 'app-propose-new-time-list',
  templateUrl: './propose-new-time-list.component.html',
  styleUrls: ['./propose-new-time-list.component.scss'],
})
export class ProposeNewTimeListComponent {
  readonly onDismiss = output<string>();
  readonly place = input.required<Place>();
  readonly oldMeeting = input.required<CalendarEvent>();

  readonly apiService = inject(ApiService);
  readonly bookingsStore = inject(BookingsStore);

  protected readonly locale = inject(DatetimeFormatService)
    .selectedDateTimeFormat;

  protected readonly isResourcelessMeeting = computed(
    () => this.oldMeeting().isResourcelessEvent === true || !this.place(),
  );

  protected readonly availabilityByProposal = computed(() => {
    if (this.isResourcelessMeeting()) {
      return {};
    }
    return this.checkAvailability(
      this.oldMeeting(),
      this.proposals(),
      this.place(),
    );
  });

  protected readonly subject = computed(() => this.oldMeeting().subject || '');

  protected readonly proposals = computed<Proposal[]>(() => {
    const attendees = this.oldMeeting().attendees;

    return attendees
      .filter((attendee) => attendee.proposedNewTime !== null)
      .map((attendee) => ({
        upn: attendee.userPrincipalName,
        name: `${attendee.firstName} ${attendee.lastName}`.trim(),
        start: attendee.proposedNewTime!.start,
        end: attendee.proposedNewTime!.end,
        img: attendee.userAvatarUrl,
        initials:
          `${attendee.firstName[0] || ''}${attendee.lastName[0] || ''}`.toUpperCase(),
        availabilityKey: `${attendee.proposedNewTime!.start || ''}-${attendee.proposedNewTime!.end || ''}`,
      }));
  });

  protected dismissModal(reason: string = '') {
    this.onDismiss.emit(reason);
  }

  protected acceptProposedTime(proposal: Proposal) {
    const oldMeeting = this.oldMeeting();
    const place = this.place();
    const isResourcelessMeeting = oldMeeting.isResourcelessEvent === true || !place;
    const eventRequest = {
      subject: oldMeeting.subject,
      startTime: proposal.start,
      endTime: proposal.end,
      reminderMinutesBeforeStart: oldMeeting.reminderMinutesBeforeStart,
      attendees: oldMeeting.attendees,
      isOnPrem: isResourcelessMeeting ? false : place.isOnPrem,
      showAs: oldMeeting.showAs,
      recurrencePattern: oldMeeting.recurrencePattern,
      timeZoneInfo: Intl.DateTimeFormat().resolvedOptions().timeZone,
      isOnlineMeeting: oldMeeting.isOnlineMeeting,
      bookingType: oldMeeting.bookingType,
      ...(isResourcelessMeeting
        ? {}
        : { placeEmailAddress: place.emailAddress }),
    };
    this.bookingsStore
      .editBooking(oldMeeting.id, false, eventRequest)
      .subscribe();
    this.onDismiss.emit('accepted');
  }

  private checkAvailability(
    oldMeeting: CalendarEvent,
    proposals: Proposal[],
    place: Place,
  ) {
    if (!proposals.length || oldMeeting.isResourcelessEvent || !place) {
      return {};
    }

    const availability: Record<string, boolean> = {};

    for (const proposal of proposals) {
      const availabilityKey = `${proposal.start || ''}-${proposal.end || ''}`;
      if (availabilityKey in availability) {
        continue;
      }
      this.apiService
        .getOnePlaceAvailability(
          new Date(proposal.start),
          new Date(proposal.end),
          oldMeeting.recurrencePattern,
          new Date(oldMeeting.startTime),
          new Date(oldMeeting.endTime),
          place,
        )
        .subscribe({
          next: (isAvailable) => {
            availability[availabilityKey] = isAvailable;
          },
          error: () => {
            availability[availabilityKey] = false;
          },
        });
    }

    return availability;
  }
}
